import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ConfiguracoesFalhaAlerta, EntidadeConfiguracao, ExibicaoConfiguracao, ExibicoesConfiguracoes, TipoConfiguracaoConnect } from 'app/interfaces';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs';
import { ComunicadorService, ConfiguracoesSyncService, DialogAgviewConnectService, ConfiguracoesService } from 'app/services';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-configuracao-falha-alerta',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './configuracao-falha-alerta.component.html',
  styleUrl: './configuracao-falha-alerta.component.css'
})
export class ConfiguracaoFalhaAlertaComponent implements OnInit {
  @Output() trocarTela = new EventEmitter<string>();

  readonly NOME_ENTIDADE_CONFIGURACAO_FALHA_ALERTA: EntidadeConfiguracao = 'FailureAlertConfigEntity';
  readonly NOME_ENTIDADE_CONFIGURACAO_MPA: EntidadeConfiguracao = 'MpaConfigEntity';

  configuracoesExibicao: ExibicoesConfiguracoes = [];

  private readonly mpaExibicaoItensExibicao: ExibicoesConfiguracoes = [
    this.criarConfiguracao('autoPlant', 'MPA - Plantio Automático', 'Ativa/Desativa o plantio automático pelo MPA.', true, true),
    this.criarConfiguracao('maxRangeLevel', 'MPA - Nível Máximo Ideal', 'Configura o nível máximo de profundidade ideal.', false, true, 'cm'),
    this.criarConfiguracao('minRangeLevel', 'MPA - Nível Mínimo Ideal', 'Configura o nível mínimo de profundidade ideal.', false, true, 'cm')
  ];
  private readonly configuracoesEstaticas = ['percentVariationDosageRateStop', 'timeSolidStopOperation', 'timeReplanting', 'timeVariationDosageRateStop','autoPlant','maxRangeLevel','minRangeLevel'];
  private readonly falhaAlertaExibicaoItensExibicao: ExibicoesConfiguracoes = [
    this.criarConfiguracao('controlSeedlingFailureEnabled', 'Controle de Falha de Muda', 'Ativa/Desativa o controle de falha de muda.', true, true),
    this.criarConfiguracao('timeReplanting', 'Tempo de Falha de Muda p/ Replantio', 'Configura o tempo de falha de muda para replantio.', false, false, 's', true),
    this.criarConfiguracao('controlSolidFailureEnabled', 'Controle de Falha de Sólido', 'Ativa/Desativa o controle de falha de sólido.', true, true),
    this.criarConfiguracao('timeSolidStopOperation', 'Tempo de Falha de Sólido p/ Parar a Operação', 'Configura o tempo de falha de p/ parar a operação.', false, false, 's', true),
    this.criarConfiguracao('controlVariationLiquidDosage', 'Controle de Variação na Dosagem de Líquido', 'Ativa/Desativa o controle de variação na dosagem de líquido.', true, true),
    this.criarConfiguracao('timeVariationDosageRateStop', 'Tempo de Variação na Dosagem p/ Parar a Operação', 'Configura o tempo de variação na taxa de dosagem p/ parar a operação.', false, false, 's', true),
    this.criarConfiguracao('percentVariationDosageRateStop', 'Variação na Dosagem p/ Parar a Operação', 'Configura a variação na taxa de dosagem p/ parar a operação.', false, false, '%', true),
    this.criarConfiguracao('scarifierSensorAlert', 'Alerta dos Sensores de Escarificador', 'Ativa/Desativa o alerta dos sensores escarificadores.', true, true),
    this.criarConfiguracao('activeStopReasonAlert', 'Alerta Motivo de Parada Ativo', 'Ativa/Desativa o alerta do motivo de parada ativo.', true, true)
  ];

  private imei = '';
  private configuracoesFalhaAlerta!: TipoConfiguracaoConnect;
  private configuracoesMPA!: TipoConfiguracaoConnect;

  constructor(
    private readonly configuracoesService: ConfiguracoesService,
    private readonly dialogAgviewConnectService: DialogAgviewConnectService,
    private configuracoesSyncService: ConfiguracoesSyncService,
    private comunicadorService: ComunicadorService
  ) { }

  ngOnInit(): void {
    this.obterConfiguracoes();
    this.atribuirConfiguracoes();
    this.atualizarConfiguracoesAoSairDaTela();
    this.observarMudancasWebscoket();
  }

  private obterConfiguracoes(): void {
    this.imei = this.configuracoesService.obterImei();
    this.configuracoesFalhaAlerta = this.configuracoesService.obterPorEntidade('FailureAlertConfigEntity');
    this.configuracoesMPA = this.configuracoesService.obterPorEntidade('MpaConfigEntity');
  }

  private criarConfiguracao(
    nomeConfiguracao: string,
    titulo: string,
    subtitulo: string,
    isToggle: boolean,
    deveSerMostrado: boolean,
    unidadeMedida?: string,
    isSubconfiguracao: boolean = false
  ): ExibicaoConfiguracao {
    return {
      nomeConfiguracao,
      titulo,
      subtitulo,
      isToggle,
      deveSerMostrado,
      unidadeMedida,
      valor: 0,
      isSubconfiguracao
    };
  }

  private atribuirConfiguracoes(): void {
    this.aplicarConfiguracoes(this.configuracoesFalhaAlerta, this.falhaAlertaExibicaoItensExibicao);
    this.configuracoesService.obterPorEntidadeWebsocket('License')
    .subscribe(() => {
      const license: string = this.configuracoesService.obterPorEntidade('License')?.licenseCode!
      const temMpa: boolean = license.includes('P1');
      this.configuracoesExibicao = temMpa
        ? [...this.aplicarConfiguracoes(this.configuracoesMPA, this.mpaExibicaoItensExibicao), ...this.falhaAlertaExibicaoItensExibicao]
        : this.falhaAlertaExibicaoItensExibicao;
    });
  }

  private aplicarConfiguracoes<T extends Record<string, any>>(
    configuracoes: T,
    exibicaoItens: ExibicaoConfiguracao[]
  ): ExibicaoConfiguracao[] {
    Object.entries(configuracoes).forEach(([nomeConfiguracao, valor]) => {
      const item = exibicaoItens.find(item => item.nomeConfiguracao === nomeConfiguracao);
      if (item) {
        item.valor = valor;
      }
    });

    this.atualizarVisibilidade();
    return exibicaoItens;
  }

  private atualizarVisibilidade(): void {
    const configuracoesDependentes = new Map([
      ['timeReplanting', 'controlSeedlingFailureEnabled'],
      ['timeSolidStopOperation', 'controlSolidFailureEnabled'],
      ['timeVariationDosageRateStop', 'controlVariationLiquidDosage'],
      ['percentVariationDosageRateStop', 'controlVariationLiquidDosage']
    ]);

    this.falhaAlertaExibicaoItensExibicao.forEach((configuracao: ExibicaoConfiguracao) => {
      const configuracaoPai = configuracoesDependentes.get(configuracao.nomeConfiguracao);
      if (configuracaoPai) {
        configuracao.deveSerMostrado = this.obterValorConfiguracao(configuracaoPai);
      }
    });
  }

  private obterValorConfiguracao(nomeConfiguracao: string): boolean {
    const configuracao = this.falhaAlertaExibicaoItensExibicao.find(
      (config: ExibicaoConfiguracao) => config.nomeConfiguracao === nomeConfiguracao
    );
    return Boolean(configuracao?.valor);
  }

  public onToggleChange(nomeConfiguracao: string, novoValor: boolean | number): void {
    const configuracao = this.falhaAlertaExibicaoItensExibicao.find(
      (config: ExibicaoConfiguracao) => config.nomeConfiguracao === nomeConfiguracao
    );

    if (configuracao) {
      configuracao.valor = novoValor;
      this.atualizarVisibilidade();
    }
  }

  public abrirDialogConfiguracaoParametro(parametro: ExibicaoConfiguracao): void {
    const indiceParametro = this.configuracoesExibicao.findIndex(item => item === parametro);

    if (indiceParametro === -1) return;

    this.dialogAgviewConnectService.open(parametro).subscribe(
      (novoValor: number) => this.configuracoesExibicao[indiceParametro].valor = novoValor
    );
  }

  public atualizarConfiguracoesAoSairDaTela(): void {
    this.configuracoesSyncService.aoDispararAtualizacao()
      .pipe(take(1))
      .subscribe(() => {
        this.atualizarConfiguracoesSeTiverAlteracao();
      });
  }

  public atualizarConfiguracoesSeTiverAlteracao(): void {
    this.obterConfiguracoes();
    const itensFalhaAlerta = this.configuracoesExibicao.filter(item =>
      this.falhaAlertaExibicaoItensExibicao.some(conf => conf.nomeConfiguracao === item.nomeConfiguracao)
        
    );
  
    const itensMpa = this.configuracoesExibicao.filter(item =>
      this.mpaExibicaoItensExibicao.some(conf => conf.nomeConfiguracao === item.nomeConfiguracao)
    );

    const configuracoesAlteradas: { FailureAlertConfigEntity?: TipoConfiguracaoConnect; MpaConfigEntity?: TipoConfiguracaoConnect } = {};
    
    const novasFalhaAlerta = this.formatarConfiguracoesParaSalvarNoBanco(itensFalhaAlerta) as ConfiguracoesFalhaAlerta;
    const antigasFalhaAlerta = this.ordenarObjeto(this.configuracoesFalhaAlerta) as ConfiguracoesFalhaAlerta;
    if (JSON.stringify(novasFalhaAlerta) !== JSON.stringify(antigasFalhaAlerta)) {
      configuracoesAlteradas.FailureAlertConfigEntity = novasFalhaAlerta;
    }
    
    const novasMpa = this.formatarConfiguracoesParaSalvarNoBanco(itensMpa);
    const antigasMpa = this.ordenarObjeto(this.configuracoesMPA);
    if ( JSON.stringify(novasMpa) !== JSON.stringify({}) && JSON.stringify(novasMpa) !== JSON.stringify(antigasMpa)) {
      configuracoesAlteradas.MpaConfigEntity = novasMpa;
    }

    if (configuracoesAlteradas.FailureAlertConfigEntity || configuracoesAlteradas.MpaConfigEntity) {
      this.atualizarConfiguracoes(configuracoesAlteradas);
    }
  }

  public formatarConfiguracoesParaSalvarNoBanco(itens: ExibicoesConfiguracoes): TipoConfiguracaoConnect | undefined {
    return this.ordenarObjeto(
      itens.filter((item: ExibicaoConfiguracao) => item.deveSerMostrado ||  this.configuracoesEstaticas.includes(item.nomeConfiguracao)).reduce((acc: any, item: any) => {
        acc[item.nomeConfiguracao] = item.valor;
        return acc;
      }, {} as Record<string, any>)
    );
  }

  public ordenarObjeto(obj: Record<string, any> | null): Record<string, any> | undefined {
    if (!obj) return undefined
    return Object.keys(obj)
      .sort()
      .reduce((sortedObj, key) => {
        sortedObj[key] = obj[key];
        return sortedObj;
      }, {} as Record<string, any>);
  }

  public atualizarConfiguracoes(configuracoes: { [entidade: string]: TipoConfiguracaoConnect }): void {
    this.comunicadorService.atualizarConfiguracoes(this.imei, configuracoes).subscribe();

    Object.entries(configuracoes).forEach(([entidade, configuracao]) => {
      this.configuracoesService.atualizarPorEntidade(entidade as EntidadeConfiguracao, configuracao as TipoConfiguracaoConnect);
    });

    this.configuracoesSyncService.dispararNotificacaoEnvio();
  }

  public irParaTelaConfigGerais(): void {
    this.atualizarConfiguracoesSeTiverAlteracao();
    this.trocarTela.emit('configuracoes');
  }

  public observarMudancasWebscoket(): void {
    this.observarMudancaEntidadeWebsocket(
      this.NOME_ENTIDADE_CONFIGURACAO_FALHA_ALERTA,
      config => {
        this.configuracoesFalhaAlerta = config;
        this.atribuirConfiguracoes();
      }
    );

    this.observarMudancaEntidadeWebsocket(
      this.NOME_ENTIDADE_CONFIGURACAO_MPA,
      config => {
        this.configuracoesMPA = config;
        this.atribuirConfiguracoes();
      }
    );
  }

  private observarMudancaEntidadeWebsocket(
    entidade: EntidadeConfiguracao,
    callback: (config: TipoConfiguracaoConnect) => void
  ): void {
    this.configuracoesService.obterPorEntidadeWebsocket(entidade)
      .subscribe((value) => {
        if (value !== undefined) {
          callback(value as TipoConfiguracaoConnect);
        }
      });
  }
}
